import { useMemo, useState } from 'react';
import { BarList, ChartCard, Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { num, shortDate, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface TimeEntry {
  id: number;
  date: string | null;
  resourceId: number | null;
  resource: string;
  projectId: number | null;
  project: string;
  projectNumber: string | null;
  projectType: string | null;
  company: string | null;
  task: string | null;
  hours: number;
  billable: number;
  notes: string;
}

type Range = 'week' | 'lastWeek' | 'month' | 'lastMonth' | '90d' | 'ytd' | 'custom';
const RANGES: { id: Range; label: string }[] = [
  { id: 'week', label: 'This week' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: '90d', label: '90 days' },
  { id: 'ytd', label: 'This year' },
  { id: 'custom', label: 'Custom' },
];

/** The days a range covers, first and last inclusive, in this browser's calendar. Weeks start Monday. */
function rangeDays(range: Exclude<Range, 'custom'>): [string, string] {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  if (range === 'week') return [toLocalDateString(monday), todayLocal()];
  if (range === 'lastWeek') {
    const start = new Date(monday);
    start.setDate(start.getDate() - 7);
    const end = new Date(monday);
    end.setDate(end.getDate() - 1);
    return [toLocalDateString(start), toLocalDateString(end)];
  }
  if (range === 'month') return [toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1)), todayLocal()];
  if (range === 'lastMonth')
    return [toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 1, 1)), toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0))];
  if (range === 'ytd') return [`${d.getFullYear()}-01-01`, todayLocal()];
  const start = new Date(d);
  start.setDate(start.getDate() - 89);
  return [toLocalDateString(start), todayLocal()];
}

/* Client or internal work, read the same way as the Autotask dashboard: a
   project type that says Internal is internal, everything else is a client's. */
const isInternal = (e: TimeEntry) => /internal/i.test(e.projectType ?? '');

const hrs = (v: number) => num(v, 2);
const sumOf = (list: TimeEntry[], k: 'hours' | 'billable') => list.reduce((n, e) => n + e[k], 0);

/**
 * Projects › Time by resource: everything logged against project tasks in
 * Autotask between two days — totals per person, and for one person, the
 * projects and the individual entries behind them. Read once per date range
 * and kept until Refresh.
 */
export function ProjectsTimeByResource() {
  const [range, setRange] = useState<Range>('lastWeek');
  const [customFrom, setCustomFrom] = useState(() => rangeDays('lastWeek')[0]);
  const [customTo, setCustomTo] = useState(() => rangeDays('lastWeek')[1]);
  const [person, setPerson] = useState('');
  const [project, setProject] = useState<number | null>(null);
  const [kind, setKind] = useState<'all' | 'client' | 'internal'>('all');

  const [from, to] = range === 'custom' ? [customFrom, customTo] : rangeDays(range);
  const valid = Boolean(from && to && from <= to);
  const data = useHeldResource<{ entries: TimeEntry[]; fetchedUtc: string }>(
    valid ? `/projects/autotask/time?${new URLSearchParams({ from, to })}` : null,
  );
  const loaded = data.data?.entries ?? [];
  /* Client or internal is applied before anything else, so every figure on
     the page — the people, their projects, the totals — is about the work
     that was asked for. */
  const all = useMemo(() => (kind === 'all' ? loaded : loaded.filter((e) => (kind === 'internal' ? isInternal(e) : !isInternal(e)))), [loaded, kind]);

  /* Projects behind each choice, so the picker says how much is on each side. */
  const kindCounts = useMemo(() => {
    const client = new Set<number | null>();
    const internal = new Set<number | null>();
    for (const e of loaded) (isInternal(e) ? internal : client).add(e.projectId);
    return { all: client.size + internal.size, client: client.size, internal: internal.size };
  }, [loaded]);

  /* Per person: hours, billable, how many projects and entries. */
  const people = useMemo(() => {
    const m = new Map<string, { name: string; hours: number; billable: number; projects: Set<number | null>; entries: number }>();
    for (const e of all) {
      const k = String(e.resourceId ?? e.resource);
      const p = m.get(k) ?? { name: e.resource, hours: 0, billable: 0, projects: new Set(), entries: 0 };
      p.hours += e.hours;
      p.billable += e.billable;
      p.projects.add(e.projectId);
      p.entries += 1;
      m.set(k, p);
    }
    return [...m].map(([key, p]) => ({ key, ...p, projects: p.projects.size })).sort((a, b) => b.hours - a.hours);
  }, [all]);

  const mine = useMemo(() => (person ? all.filter((e) => String(e.resourceId ?? e.resource) === person) : all), [all, person]);
  const byProject = useMemo(() => {
    const m = new Map<string, { id: number | null; name: string; company: string | null; hours: number; billable: number }>();
    for (const e of mine) {
      const k = String(e.projectId);
      const p = m.get(k) ?? { id: e.projectId, name: e.project, company: e.company, hours: 0, billable: 0 };
      p.hours += e.hours;
      p.billable += e.billable;
      m.set(k, p);
    }
    return [...m.values()].sort((a, b) => b.hours - a.hours);
  }, [mine]);
  const shown = useMemo(
    () =>
      mine
        .filter((e) => project === null || e.projectId === project)
        .sort((a, b) => String(b.date).localeCompare(String(a.date)) || a.resource.localeCompare(b.resource)),
    [mine, project],
  );

  const hours = sumOf(mine, 'hours');
  const billable = sumOf(mine, 'billable');
  const who = people.find((p) => p.key === person);

  const exportCsv = () =>
    downloadCsv(`project_time_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Date', 'Resource', 'Project', 'Project #', 'Company', 'Task', 'Hours worked', 'Billable hours', 'Notes'],
      ...shown.map((e) => [e.date, e.resource, e.project, e.projectNumber, e.company, e.task, e.hours, e.billable, e.notes]),
    ]);

  const pickPerson = (key: string) => {
    setPerson(person === key ? '' : key);
    setProject(null);
  };

  return (
    <div className="mt-4">
      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
            <Field label="Worked">
              <Segmented label="Worked" options={RANGES} value={range} onChange={(v) => { setRange(v); setProject(null); }} />
            </Field>
            {range === 'custom' && (
              <>
                <Field label="From">
                  <input className="input" type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
                </Field>
                <Field label="To">
                  <input className="input" type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
                </Field>
              </>
            )}
            <Field label="Work">
              <Segmented
                label="Client or internal"
                options={[
                  { id: 'all' as const, label: `All (${kindCounts.all})` },
                  { id: 'client' as const, label: `Client (${kindCounts.client})` },
                  { id: 'internal' as const, label: `Internal (${kindCounts.internal})` },
                ]}
                value={kind}
                onChange={(v) => {
                  setKind(v);
                  setPerson('');
                  setProject(null);
                }}
              />
            </Field>
            <Field label="Resource">
              <select className="select" value={person} onChange={(e) => pickPerson(e.target.value)} disabled={!data.data}>
                <option value="">Everyone</option>
                {[...people].sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing || !valid} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={shown.length === 0} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
          <p className="xs muted mt-2">
            {kind === 'all' ? 'Time entered against project tasks' : `Time entered against ${kind} project tasks`},{' '}
            {shortDate(from)} – {shortDate(to)}. Ticket time is not included.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {!valid && <ErrorBanner message="The start date is after the end date." />}
      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading project time from Autotask" />}

      {data.data && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Hours worked" value={hrs(hours)} foot={who ? who.name : `${people.length} ${people.length === 1 ? 'person' : 'people'}`} />
            <Stat label="Billable" value={hrs(billable)} foot={hours ? `${Math.round((billable / hours) * 100)}% of hours worked` : '—'} />
            <Stat label="Projects" value={num(byProject.length)} foot="with time in this period" />
            <Stat label="Entries" value={num(mine.length)} foot="time entries" />
          </div>

          {all.length === 0 ? (
            <section className="card">
              <div className="card-body">
                <p className="muted small">
                  {kind === 'all'
                    ? 'No project time was entered in this period.'
                    : `No time was entered against ${kind} projects in this period.`}
                </p>
              </div>
            </section>
          ) : (
            <>
              <div className="grid grid-2 mb-4">
                <ChartCard title="Hours by resource" subtitle="Click a person to see what they worked on.">
                  <div className="pa-pick">
                    {people.map((p) => (
                      <button key={p.key} type="button" className={person === p.key ? 'is-on' : undefined} onClick={() => pickPerson(p.key)}>
                        <BarList rows={[{ name: p.name, value: p.hours, display: `${hrs(p.hours)} h · ${hrs(p.billable)} billable` }]} />
                      </button>
                    ))}
                  </div>
                </ChartCard>
                <ChartCard title={`Hours by project${who ? ` — ${who.name}` : ''}`} subtitle="Click a project to list only its entries.">
                  <div className="pa-pick">
                    {byProject.map((p) => (
                      <button
                        key={String(p.id)}
                        type="button"
                        className={project === p.id ? 'is-on' : undefined}
                        onClick={() => setProject(project === p.id ? null : p.id)}
                      >
                        <BarList rows={[{ name: p.company ? `${p.name} · ${p.company}` : p.name, value: p.hours, display: `${hrs(p.hours)} h` }]} />
                      </button>
                    ))}
                  </div>
                </ChartCard>
              </div>

              {!person && (
                <section className="card mb-4">
                  <div className="card-head">
                    <div className="grow">
                      <h3>By resource</h3>
                      <p className="sub">Click a name for their projects and entries.</p>
                    </div>
                  </div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Resource</th>
                          <th className="num">Hours worked</th>
                          <th className="num">Billable</th>
                          <th className="num">Projects</th>
                          <th className="num">Entries</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p) => (
                          <tr key={p.key}>
                            <td>
                              <button type="button" className="link-button" onClick={() => pickPerson(p.key)}>
                                {p.name}
                              </button>
                            </td>
                            <td className="num">{hrs(p.hours)}</td>
                            <td className="num">
                              {hrs(p.billable)}
                              {p.hours > 0 && <div className="xs muted">{Math.round((p.billable / p.hours) * 100)}%</div>}
                            </td>
                            <td className="num">{num(p.projects)}</td>
                            <td className="num">{num(p.entries)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <section className="card">
                <div className="card-head">
                  <div className="grow">
                    <h3>
                      {num(shown.length)} entr{shown.length === 1 ? 'y' : 'ies'}
                      {who ? ` — ${who.name}` : ''}
                      {project !== null ? ` — ${byProject.find((p) => p.id === project)?.name ?? ''}` : ''}
                    </h3>
                    <p className="sub">Newest first.</p>
                  </div>
                  {(person || project !== null) && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setPerson(''); setProject(null); }}>
                      Show everyone
                    </button>
                  )}
                </div>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        {!person && <th>Resource</th>}
                        <th>Project</th>
                        <th>Task</th>
                        <th className="num">Hours</th>
                        <th className="num">Billable</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.slice(0, 1000).map((e) => (
                        <tr key={e.id}>
                          <td className="xs" style={{ whiteSpace: 'nowrap' }}>{e.date ? shortDate(e.date) : '—'}</td>
                          {!person && <td>{e.resource}</td>}
                          <td>
                            {e.project}
                            <div className="xs muted">{[e.projectNumber, e.company].filter(Boolean).join(' · ')}</div>
                          </td>
                          <td>{e.task ?? '—'}</td>
                          <td className="num">{hrs(e.hours)}</td>
                          <td className="num">{hrs(e.billable)}</td>
                          <td className="xs" style={{ maxWidth: 360 }}>{e.notes || <span className="subtle">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {shown.length > 1000 && (
                  <div className="card-body">
                    <p className="xs muted">Showing the newest 1,000. Export CSV has all {num(shown.length)}.</p>
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}
